/**
 * The loader half of the demo-data profile. While enabled, any dataset that
 * came from D1 (source 'api') is replaced by the bundled JSON for the same
 * network, now and whenever the loader runtime loads one again (network
 * switch, a D1 answer that was still in flight). Disabling reloads the
 * network the normal way (D1, then the bundled fallback). Live
 * constellations (source 'live') have no bundled missions and are left alone.
 * A bundled load that fails is reported once and not retried for the same
 * dataset; a new D1 dataset (a reload, a network switch) gets one more try.
 *
 * Store fields written: useLoader.dataset through setDataset, which also sets
 * datasetStatus, datasetError, toggles, activeMissionIds, activeAntennaIds and
 * bumps analysisRequest. The loader runtime's D1 sync poll only runs for
 * 'api' datasets, so it stops while the bundled data is in.
 *
 * A swap redraws the globe, which drops a followed satellite while
 * useLoader.trackedMissionId still names it (LoaderGlobe re-tracks only when
 * that field changes). So a moment after each swap the engine is asked to
 * follow the mission again, directly and without touching the store, and only
 * if the store still names that mission: a beat that stopped the follow or a
 * network switch in between wins.
 */
import type { NetworkDataset, NetworkId } from '@/features/loader/types';

/** Delay before the re-follow, so the redraw (LoaderGlobe's data effect) has run. */
export const REFOLLOW_DELAY_MS = 100;

export interface DemoLoaderPort {
  network: NetworkId;
  dataset: Pick<NetworkDataset, 'network' | 'source'> | null;
  trackedMissionId: string | null;
  setDataset: (dataset: NetworkDataset) => void;
}

/** The two GlobeEngine calls the re-follow needs. */
export interface DemoGlobePort {
  track(missionId: string | null): void;
  getTracked(): string | null;
}

export interface DemoDataDeps {
  loader(): DemoLoaderPort;
  /** Called on every loader store change. */
  subscribe(listener: () => void): () => void;
  /** The bundled JSON for a network (loadStaticDataset). */
  loadBundled(network: NetworkId): Promise<NetworkDataset>;
  /** The normal load order: D1, then the bundled fallback (loadNetworkDataset). */
  loadNetwork(network: NetworkId): Promise<NetworkDataset>;
  /** Loud report of a failed load (toast). */
  report(text: string): void;
  /** The globe engine, when a globe mode has it mounted. */
  globe(): DemoGlobePort | null;
  /** Runs fn after ms (setTimeout). */
  defer(fn: () => void, ms: number): void;
}

export interface DemoDataController {
  setEnabled(enabled: boolean): void;
  isEnabled(): boolean;
  /** Stops watching without reloading anything (session teardown). */
  dispose(): void;
}

export function createDemoData(deps: DemoDataDeps): DemoDataController {
  let enabled = false;
  let unsubscribe: (() => void) | null = null;
  /** Dataset whose bundled replacement is being fetched, so repeated store changes do not refetch. */
  let pending: object | null = null;
  /** Dataset whose bundled replacement failed to load: not retried until another dataset arrives. */
  let failedFor: object | null = null;

  const fail = (what: string, network: NetworkId, err: unknown) => {
    const message = err instanceof Error ? err.message : String(err);
    console.error(`[presenter] demo data: ${what} for ${network} failed:`, err);
    deps.report(`Demo data: ${what} for ${network} failed: ${message}`);
  };

  /** Swaps the dataset in, then re-arms on the engine a follow the redraw drops. */
  const swapIn = (dataset: NetworkDataset) => {
    const loader = deps.loader();
    const followed = loader.trackedMissionId;
    loader.setDataset(dataset);
    if (!followed) return;
    deps.defer(() => {
      if (deps.loader().trackedMissionId !== followed) return;
      const globe = deps.globe();
      if (globe && globe.getTracked() !== followed) globe.track(followed);
    }, REFOLLOW_DELAY_MS);
  };

  const enforce = () => {
    if (!enabled) return;
    const dataset = deps.loader().dataset;
    if (!dataset || dataset.source !== 'api' || dataset === pending || dataset === failedFor) return;
    const network = dataset.network;
    pending = dataset;
    deps
      .loadBundled(network)
      .then((bundled) => {
        if (pending === dataset) pending = null;
        // Turned off, or the dataset this was for has been replaced (a patch, a reload, another network)
        // while the files were loading: a newer request, or the newer data itself, wins. The selected
        // network is not compared: a live constellation that fell back to NOAA holds NOAA data under it.
        if (!enabled || deps.loader().dataset !== dataset) return;
        swapIn(bundled);
      })
      .catch((err: unknown) => {
        if (pending === dataset) pending = null;
        failedFor = dataset;
        fail('loading the bundled data', network, err);
      });
  };

  const restore = () => {
    const loader = deps.loader();
    const bundled = loader.dataset;
    if (bundled?.source !== 'static') return;
    const network = loader.network;
    deps
      .loadNetwork(network)
      .then((dataset) => {
        // Turned back on, or the bundled data was replaced or edited meanwhile: that newer state wins.
        const current = deps.loader();
        if (enabled || current.network !== network || current.dataset !== bundled) return;
        swapIn(dataset);
      })
      .catch((err: unknown) => fail('reloading the network data', network, err));
  };

  return {
    setEnabled(next) {
      if (next === enabled) return;
      enabled = next;
      pending = null;
      failedFor = null;
      if (enabled) {
        unsubscribe = deps.subscribe(enforce);
        enforce();
      } else {
        unsubscribe?.();
        unsubscribe = null;
        restore();
      }
    },
    isEnabled: () => enabled,
    dispose() {
      unsubscribe?.();
      unsubscribe = null;
      enabled = false;
      pending = null;
      failedFor = null;
    },
  };
}
